/**
 * Hawaii Tee Times API Integration
 * CRITICAL: This manages all real-time booking functionality
 */

class TeeTimesAPI {
  constructor() {
    this.apiEndpoint = window.HTT?.apiEndpoint || 'https://hawaii-tee-times-dev.replit.app/api';
    this.courseId = null;
    this.blackoutDates = [];
    this.variants = [];
    this.init();
  }

  init() {
    // Skip initialization for FT products - they use BookThatApp only
    if (this.isFTProduct()) {
      console.log('⚠️ FT product detected - skipping Real-Time API initialization');
      return;
    }

    // Extract course ID from tags
    if (window.productTags) {
      const courseTag = window.productTags.find(tag => tag.startsWith('courseid:'));
      if (courseTag) {
        this.courseId = courseTag.slice(9);
      }
    }

    // Initialize variants
    if (window.productVariants) {
      this.variants = this.processVariants(window.productVariants);
      this.blackoutDates = this.getBlackoutDates();
    }
  }

  isFTProduct() {
    // Check if this is an FT product by title or tags
    if (window.productTitle) {
      if (window.productTitle.includes('FT') || window.productTitle.includes('ft')) {
        return true;
      }
    }
    if (window.productTags) {
      return window.productTags.some(tag =>
        tag === 'FT' || tag === 'ForeTees' || tag === 'foretees'
      );
    }
    return false;
  }

  processVariants(variants) {
    return variants.filter(v => {
      return v.price !== 999900 && v.title !== 'Blackout';
    }).map(v => ({
      id: v.id,
      price: v.price / 100,
      priceFormatted: this.formatMoney(v.price),
      name: v.title,
      timeRanges: this.parseTimeRanges(v.metafields?.shoppad?.time_range),
      priority: parseInt(v.metafields?.shoppad?.priority || 999),
      dateRange: v.metafields?.shoppad?.date_range || '',
      dayOfWeek: (v.metafields?.shoppad?.day_of_week || '').trim().toUpperCase(),
      notes: v.metafields?.shoppad?.notes || ''
    }));
  }

  parseTimeRanges(timeRangeStr) {
    if (!timeRangeStr) return [];
    return timeRangeStr.split(',').map(range => {
      const [start, end] = range.split('-').map(t => parseInt(t));
      return [start, end];
    });
  }

  getBlackoutDates() {
    const blackouts = [];
    const blackoutVariants = window.productVariants?.filter(v =>
      v.price === 999900 || v.title === 'Blackout'
    ) || [];

    blackoutVariants.forEach(variant => {
      const dateRanges = (variant.metafields?.shoppad?.date_range || '').split(',');
      dateRanges.forEach(range => {
        const [start, end] = range.split('-').map(d => new Date(d));
        if (start && end) {
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            blackouts.push(this.formatDateString(d));
          }
        }
      });
    });

    return blackouts;
  }

  formatDateString(date) {
    return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
  }

  async fetchAvailableTimes(date) {
    if (!this.courseId) {
      throw new Error('Course ID not found');
    }

    const timestamp = this.getUTCTimestamp(date);
    const response = await fetch(`${this.apiEndpoint}/courses/${window.productId}/times?date=${timestamp}`);

    if (!response.ok) {
      throw new Error('Failed to fetch available times');
    }

    return response.json();
  }

  async reserveTime(timeData, playerCount) {
    // Update player count in reservation string if needed
    if (timeData.reserveString) {
      const parts = timeData.reserveString.split(',');
      parts[2] = playerCount;
      parts[5] = playerCount;
      timeData.reserveString = parts.join(',');
    }

    // Generate reservation ID
    const strBuffer = new TextEncoder().encode(JSON.stringify(timeData));
    const hashBuffer = await crypto.subtle.digest('SHA-256', strBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 8);

    // Reserve the time slot
    const response = await fetch(`${this.apiEndpoint}/times/reserve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: hashHex,
        data: timeData
      })
    });

    if (!response.ok) {
      throw new Error('Failed to reserve time slot');
    }

    return hashHex;
  }

  filterTimesByVariants(times, selectedDate) {
    const currentTime = this.getHawaiiTime();
    const cutoffTime = new Date(currentTime.getTime() + 45 * 60000); // 45 min cutoff

    return times.filter(time => {
      const timeDate = new Date(time.date);

      // Check cutoff time
      if (timeDate.getTime() <= cutoffTime.getTime()) {
        return false;
      }

      // Find matching variant
      const variant = this.findMatchingVariant(timeDate);
      if (!variant) {
        return false;
      }

      // Add variant info to time object
      time.variant = variant;
      time.displayPrice = time.price || variant.price;

      return true;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  findMatchingVariant(date) {
    const timenum = parseInt(`${date.getUTCHours()}${date.getUTCMinutes().toString().padStart(2, '0')}`);

    // Filter by time range
    let matchingVariants = this.variants.filter(v =>
      v.timeRanges.some(range => range[0] <= timenum && range[1] >= timenum)
    );

    // Filter by date and day
    matchingVariants = matchingVariants.filter(v =>
      this.checkVariantDate(v, date) && this.checkVariantDay(v, date)
    );

    // Sort by priority and return highest
    matchingVariants.sort((a, b) => b.priority - a.priority);
    return matchingVariants[0];
  }

  checkVariantDate(variant, date) {
    const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const dateRanges = variant.dateRange.split(',');

    for (const range of dateRanges) {
      const [start, end] = range.split('-');
      const startDate = new Date(start);
      const endDate = end ? new Date(end) : startDate;

      if (utcDate >= startDate && utcDate <= endDate) {
        return true;
      }
    }

    return false;
  }

  checkVariantDay(variant, date) {
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const dayOfWeek = days[date.getUTCDay()];
    const variantDays = variant.dayOfWeek.replace(/\s+/g, '').split(',');

    return variantDays.includes(dayOfWeek) || variantDays.includes('ALL');
  }

  getHawaiiTime() {
    const now = new Date();
    const hawaiiOffset = -10; // Hawaii Standard Time (UTC-10)
    return new Date(now.getTime() + hawaiiOffset * 60 * 60 * 1000);
  }

  getUTCTimestamp(date) {
    const localDate = new Date(date);
    return localDate.getTime() - (localDate.getTimezoneOffset() * 60000);
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  isBlackoutDate(date) {
    const dateStr = this.formatDateString(date);
    return this.blackoutDates.includes(dateStr);
  }

  // Handle Google conversion tracking
  trackConversion() {
    if (!window.productTags?.includes('googletracking')) return;

    const params = new URLSearchParams(window.location.search);
    const rwgToken = params.get('rwg_token');

    if (rwgToken) {
      document.cookie = `_rwg_token=${rwgToken};max-age=2592000;path=/;domain=hawaiiteetimes.com`;
    }

    const rwgTokenCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('_rwg_token='));

    if (rwgTokenCookie) {
      const token = rwgTokenCookie.split('=')[1];
      const partnerId = window.HTT?.googlePartnerId || 20003390;

      fetch('https://www.google.com/maps/conversion/collect', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          conversion_partner_id: partnerId,
          rwg_token: token,
          merchant_changed: '2'
        })
      }).catch(console.error);
    }
  }
}

// Initialize API when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.teeTimesAPI = new TeeTimesAPI();
  window.TeeTimesAPI = TeeTimesAPI; // Make class available for validation
});